import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect } from "@/components/common";
import { DatePicker } from "@/components/DatePicker";
import { cn } from "@/lib/utils";
import { Home, Building2, Landmark } from "lucide-react";

const ICONS = { residential: Home, corporate: Building2, government: Landmark };

export function ClassificationPicker({ schemas, value, onChange }) {
  return (
    <div className="grid gap-3" data-testid="classification-picker">
      {schemas.map((s) => {
        const Icon = ICONS[s.classification];
        const active = value === s.classification;
        return (
          <button type="button" key={s.classification} onClick={() => onChange(s.classification)} data-testid={`classification-option-${s.classification}`}
            className={cn("text-left flex gap-4 p-4 rounded-lg border-2 transition-colors bg-white",
              active ? "border-amber-500 bg-amber-50/60" : "border-slate-200 hover:border-amber-300")}>
            <div className={cn("h-10 w-10 rounded-md grid place-items-center shrink-0", active ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600")}><Icon className="h-5 w-5" /></div>
            <div>
              <p className="font-semibold text-slate-900">{s.label}</p>
              <p className="text-sm text-slate-500">{s.description}</p>
            </div>
          </button>
        );
      })}
      <p className="text-xs text-slate-500">The account type identifies your application. It does not determine tax treatment or withholding-agent status — those are assessed separately by CASURECO II Accounting.</p>
    </div>
  );
}

export function isRequired(f, data) {
  if (f.required) return true;
  return !!f.required_if && Object.entries(f.required_if).every(([k, v]) => data[k] === v);
}

export function validateFields(fields, data) {
  const errs = {};
  fields.forEach((f) => {
    const v = data[f.key];
    const empty = v === undefined || v === null || v === "" || (f.type === "checkbox" && v !== true);
    if (empty) { if (isRequired(f, data)) errs[f.key] = `${f.label} is required`; return; }
    if (f.type !== "checkbox" && f.pattern && !new RegExp(`^(?:${f.pattern.replace(/^\^|\$$/g, "")})$`).test(String(v).trim())) errs[f.key] = f.pattern_message || "Invalid format";
  });
  return errs;
}

export function DynamicField({ field: f, value, onChange, error, data, disabled }) {
  const req = isRequired(f, data);
  const id = `field-${f.key}`;
  const label = (
    <Label htmlFor={id} className="text-sm">
      {f.label}{req ? <span className="text-rose-600"> *</span> : <span className="text-slate-400 text-xs font-normal"> (optional)</span>}
    </Label>
  );
  let input;
  if (f.type === "checkbox") {
    return (
      <div className="sm:col-span-2 flex items-start gap-3" data-testid={`${id}-wrap`}>
        <Checkbox id={id} checked={value === true} disabled={disabled} onCheckedChange={(c) => onChange(c === true)} data-testid={id} />
        <div>{label}{error && <p className="text-xs text-rose-600 mt-1" data-testid={`${id}-error`}>{error}</p>}</div>
      </div>
    );
  }
  if (f.type === "select") input = <NativeSelect testId={id} disabled={disabled} value={value} onChange={onChange} options={f.options} placeholder="Select…" />;
  else if (f.type === "textarea") input = <Textarea id={id} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  else if (f.type === "date") input = <DatePicker value={value || ""} onChange={onChange} disabled={disabled} testId={id} captionLayout="dropdown-buttons" />;
  else input = <Input id={id} disabled={disabled} type={f.type === "email" ? "email" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  return (
    <div className={cn("space-y-1.5", (f.type === "textarea" || f.max_length >= 300) && "sm:col-span-2")}>
      {label}{input}
      {f.help && !error && <p className="text-xs text-slate-500">{f.help}</p>}
      {error && <p className="text-xs text-rose-600" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
}

export const sectionsOf = (schema) => [...new Set(schema.fields.map((f) => f.section))];
